import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Space, message, Pagination } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import { API_URL, TOKEN } from "../../../variables/constants";
import AddCarosserie from "./AddCarosserie";
import ActionDropdown from "../../production/components/ActionDropdown";

const Carosseries = () => {
  const [carosseries, setCarosseries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedCarosserie, setSelectedCarosserie] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false); // State for table loading

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Allow changing page size, default 10

  const handleShow = (edit = false, carosserie = null) => {
    setEditMode(edit);
    setSelectedCarosserie(carosserie);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedCarosserie(null);
  };

  const fetchCarosseries = async () => {
    setLoadingTable(true); // Start loading
    try {
      const res = await axios.get(`${API_URL}/api/parametre/type/CAROSSERIE`, {
        headers: { Authorization: `Bearer ${TOKEN}` },
      });
      // Sort by parametreId in descending order to show latest first
      // If your API provides a 'createdAt' timestamp, use that for more accurate "latest" sorting.
      const sortedCarosseries = res.data.sort((a, b) => b.parametreId - a.parametreId);
      setCarosseries(sortedCarosseries);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des carrosseries.");
    } finally {
      setLoadingTable(false); // End loading
    }
  };

  useEffect(() => {
    fetchCarosseries();
  }, []); // Fetch on initial component mount only

  // --- Handlers for Add and Update to keep new/modified at top ---
  const handleAddSuccess = (newCarrosserie) => {
    setCarosseries((prev) => [newCarrosserie, ...prev]); // Add new at the top
    setCurrentPage(1); // Go to the first page to see the new item
  };

  const handleUpdateSuccess = (updatedCarrosserie) => {
    setCarosseries((prev) => {
      const withoutOld = prev.filter(
        (car) => car.parametreId !== updatedCarrosserie.parametreId
      );
      return [updatedCarrosserie, ...withoutOld]; // Place updated at the top
    });
    // The current page usually doesn't need to change for an update,
    // as the item will simply move to the top of the list, which might be on the current page or page 1.
  };
  // --- End Handlers ---

  const handleDelete = (parametreId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cette carrosserie ?",
      content: "Cette action est irréversible",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axios.delete(`${API_URL}/api/parametre/${parametreId}`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          });
          setCarosseries((prev) =>
            prev.filter((c) => c.parametreId !== parametreId)
          );
          message.success("Carrosserie supprimée avec succès.");

          // After deletion, check if the current page is now empty
          // or if the last item on the last page was deleted.
          const totalItemsAfterDeletion = carosseries.length - 1;
          const totalPagesAfterDeletion = Math.ceil(totalItemsAfterDeletion / pageSize);
          if (currentPage > totalPagesAfterDeletion && totalPagesAfterDeletion > 0) {
            setCurrentPage(totalPagesAfterDeletion);
          } else if (totalPagesAfterDeletion === 0) {
            setCurrentPage(1); // Reset to page 1 if all items are deleted
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression.");
        }
      },
    });
  };

  // Pagination logic
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = carosseries.slice(startIndex, endIndex);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow()}
              >
                Ajouter une nouvelle carrosserie
              </Button>

              <div className="card">
                <h5 className="card-header">Paramétrage des carrosseries</h5>
                <div className="table-responsive text-nowrap">
                  {loadingTable ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : paginatedData.length > 0 ? (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Catégorie</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="table-border-bottom-0">
                        {paginatedData.map((car) => (
                          <tr key={car.parametreId}>
                            <td>{car.libelleParametre}</td>
                            <td>{car.categorie?.nom}</td>
                            <td>
                              <ActionDropdown
                              actions={[
                                {
                                  key: "edit",
                                  label: "Modifier",
                                  icon: <i className="bx bx-edit-alt me-1" />,
                                  onClick: () => handleShow(true, car),
                                },
                                {
                                  key: "delete",
                                  label: "Supprimer",
                                  icon: <i className="bx bx-trash me-1" />,
                                  danger: true,
                                  onClick: () => handleDelete(car.parametreId),
                                },
                              ]}
                            />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center p-3">Aucune carrosserie trouvée.</p>
                  )}
                  {carosseries.length > pageSize && ( // Only show pagination if there's more than one page
                    <div className="d-flex justify-content-end me-3 mb-3 mt-3">
                      <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={carosseries.length}
                        onChange={(page, size) => { // Allow changing page size
                          setCurrentPage(page);
                          setPageSize(size);
                        }}
                        showSizeChanger // Enable size changer
                        pageSizeOptions={['5', '10', '20', '50']} // Options for page size
                        showTotal={(total, range) => `${range[0]}-${range[1]} sur ${total} éléments`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddCarosserie
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedCarrosserie={selectedCarosserie}
        onAddSuccess={handleAddSuccess}
        onUpdateSuccess={handleUpdateSuccess}
        existingCarosseries={carosseries} // Pass the full list for client-side duplicate check
      />
    </div>
  );
};

export default Carosseries;